import { useMutation, useQuery } from '@tanstack/react-query';
import { NumbersAPI } from 'api/cards';
import LoadingSpinner from 'components/loader/LoadingSpinner';
import { NumbersFormVariables } from 'components/module/cards/NumbersCard';
import SituationBuilder from 'components/resources/triggers/SituationBuilder';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from 'components/utils/AvoPopover';
import Button from 'components/utils/Button';
import Checkbox from 'components/utils/Checkbox';
import { ComboBox } from 'components/utils/ComboBox';
import DropdownSingle from 'components/utils/common/DropdownSingle';
import { useToolOptions } from 'components/utils/example-n-tools/useToolOptions';
import { ExpandableList } from 'components/utils/form-input/ExpandableList';
import Input from 'components/utils/Input';
import Label from 'components/utils/Label';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { AvoSlideCloseModal } from 'components/utils/panels/AvoSlideCloseModal';
import { SlidePanel } from 'components/utils/panels/AvoSlidePanel';
import RequiredMark from 'components/utils/requiredMark';
import { StaffOnlyBadgeType, StaffOnlyWrapper } from 'components/utils/StaffOnlyWrapper';
import { Body1, Body2, Caption2, H4 } from 'components/utils/typo';
import { useTriggers } from 'hooks/module/resources/useTriggers';
import { ChevronDown, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { CustomNumbersForm } from '../resources/custom-numerics/CustomNumbersForm';

interface NumbersFormProps {
  elementId: number;
  onSave: () => void;
  onCancel: () => void;
}

export const NumbersForm = ({ elementId, onSave, onCancel }: NumbersFormProps) => {
  const { module } = useContext(ModuleContext);
  const [triggerFormOpen, setTriggerFormOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [customNumberId, setCustomNumberId] = useState<number | undefined>(undefined);
  const [customNumbersFormOpen, setCustomNumbersFormOpen] = useState(false);
  const {
    watch,
    register,
    setValue,
    handleSubmit,
    setError,
    formState: { isDirty, errors },
    reset,
  } = useFormContext<NumbersFormVariables>();

  const { data: triggers } = useTriggers();
  const [triggerId, setTriggerId] = useState<number | undefined>();
  const triggerOptions = triggers.map((trigger) => ({
    value: trigger.id,
    label: trigger.title,
    isEditingDisabled: trigger.candidate_trigger || trigger.is_global,
  }));
  const triggerComponentProps = {
    type: module?.type,
    toggleModal: () => setTriggerFormOpen(false),
    selectTrigger: (value) => setValue('trigger', value, { shouldDirty: true }),
    createModal: true,
    triggerId: triggerId,
    // modal: triggerModalOpen,
    savePosition: () => {},
    // toogleModalPane: () => setTriggerModalOpen(false),
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: !!elementId
      ? (data: NumbersFormVariables) => NumbersAPI.patch(elementId, data)
      : (data: NumbersFormVariables) => NumbersAPI.post(data),
  });
  const { infoboxOptions, calcOptions } = useToolOptions();

  const onSubmit = async (data: NumbersFormVariables) => {
    if (isPending) return;
    if (!data.panel_item.length) {
      setError('panel_item', { type: 'required', message: 'At least one number is required' });
      return;
    }
    isDirty && (await mutateAsync(data));
    reset(data);
    onSave();
  };
  return (
    <>
      <form className='flex h-full flex-col' onSubmit={handleSubmit(onSubmit)}>
        <div className='grow overflow-y-auto'>
          <div className='sticky top-0 z-10 flex items-center justify-between bg-white pl-4 pr-3 pt-[20px]'>
            <H4>Numbers</H4>
            <div
              className='cursor-pointer'
              onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
            >
              <X size={20} />
            </div>
          </div>
          <div className='mt-4 px-4'>
            <div className='space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Trigger</Body2>
                <RequiredMark />
              </div>
              <ComboBox
                onChange={(value: any) => setValue('trigger', value, { shouldDirty: true })}
                options={triggerOptions}
                selectedValue={watch('trigger')}
                onCreate={() => {
                  setTriggerId(undefined);
                  setTriggerFormOpen(true);
                }}
                onEdit={(id) => {
                  setTriggerId(+id);
                  setTriggerFormOpen(true);
                }}
                createButtonLabel='Create New Trigger'
              />
            </div>
            <div className='mt-6 space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Title</Body2>
                <RequiredMark />
              </div>
              <Input
                {...register('title', { required: true, maxLength: 255 })}
                required
                maxLength={255}
              />
              <Caption2 className='flex justify-end text-gray-text-weak'>
                {watch('title').length}/255
              </Caption2>
            </div>
            <div className='mt-6'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Numbers</Body2>
                <RequiredMark />
              </div>
              <div className='mt-2 flex gap-1'>
                <CustomNumberButton
                  onCustomNumberCreate={() => {
                    setCustomNumberId(undefined);
                    setCustomNumbersFormOpen(true);
                  }}
                />
                <PredefinedNumberButton />
              </div>
              {errors.panel_item && <div className='text-error'>{errors.panel_item.message}</div>}
              {!!watch('panel_item').length && (
                <div className='mt-3'>
                  <ExpandableList
                    setValue={(value) => setValue('panel_item', value, { shouldDirty: true })}
                    addItemLabel='Add Data'
                    items={watch('panel_item')}
                  >
                    <ExpandableList.Items>
                      {watch('panel_item').map((item, index) => (
                        <ExpandableList.Item index={index} key={index}>
                          <ExpandableList.Button>
                            {(open) => {
                              return (
                                <div className='flex w-full items-center space-x-[8px] px-[16px] py-[14px] focus:bg-transparent'>
                                  <ChevronDown
                                    className={twMerge(open && 'rotate-180')}
                                    size={16}
                                  />
                                  <Body1 className='grow'>{item.title}</Body1>
                                  {item.numeric_type === 'custom' && (
                                    <Pencil
                                      size={16}
                                      className='cursor-pointer text-gray-600'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCustomNumberId(item.id);
                                        setCustomNumbersFormOpen(true);
                                      }}
                                    />
                                  )}
                                  <Trash2
                                    size={16}
                                    className='cursor-pointer text-gray-600'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setValue(
                                        'panel_item',
                                        watch('panel_item').filter(
                                          (_, itemIndex) => itemIndex !== index
                                        ),
                                        { shouldDirty: true }
                                      );
                                    }}
                                  />
                                </div>
                              );
                            }}
                          </ExpandableList.Button>
                          <ExpandableList.Panel>
                            <div className='ml-6 space-y-3'>
                              <Input
                                placeholder='Default Value'
                                type='number'
                                value={item.default_value || ''}
                                onChange={(e) =>
                                  setValue(
                                    'panel_item',
                                    watch('panel_item').map((item, itemIndex) =>
                                      itemIndex === index
                                        ? { ...item, default_value: e.target.value || undefined }
                                        : item
                                    ),
                                    { shouldDirty: true }
                                  )
                                }
                              />
                              <StaffOnlyWrapper type={StaffOnlyBadgeType.BETA}>
                                <DropdownSingle
                                  options={calcOptions
                                    .filter(
                                      (calc) =>
                                        calc.type === 'heading' ||
                                        (calc.output &&
                                          ['B', 'N'].includes(calc.output.output_type) &&
                                          !!calc.output.output_value)
                                    )
                                    .map((option) => ({
                                      ...option,
                                      title: option.label,
                                    }))}
                                  value={item.calculator || ''}
                                  onChange={(calcId) =>
                                    setValue(
                                      'panel_item',
                                      watch('panel_item').map((item, i) =>
                                        i === index ? { ...item, calculator: Number(calcId) } : item
                                      ),
                                      { shouldDirty: true }
                                    )
                                  }
                                  placeholder='Select Calculator'
                                />
                              </StaffOnlyWrapper>
                              <DropdownSingle
                                options={infoboxOptions.map((option) => ({
                                  ...option,
                                  title: option.label,
                                }))}
                                value={item.infobox || ''}
                                onChange={(infoboxId) => {
                                  setValue(
                                    'panel_item',
                                    watch('panel_item').map((item, i) =>
                                      i === index ? { ...item, infobox: Number(infoboxId) } : item
                                    ),
                                    { shouldDirty: true }
                                  );
                                }}
                                placeholder='Select Infobox'
                              />
                              <Label className='flex gap-1'>
                                <Checkbox
                                  checked={item.is_optional}
                                  onChange={(e) =>
                                    setValue(
                                      'panel_item',
                                      watch('panel_item').map((item, i) =>
                                        i === index
                                          ? { ...item, is_optional: e.target.checked }
                                          : item
                                      ),
                                      {
                                        shouldDirty: true,
                                      }
                                    )
                                  }
                                />
                                <Body2>Make this input optional</Body2>
                              </Label>
                            </div>
                          </ExpandableList.Panel>
                        </ExpandableList.Item>
                      ))}
                    </ExpandableList.Items>
                  </ExpandableList>
                </div>
              )}
            </div>
            <div className='mt-6'>
              <Label className='flex gap-1'>
                <Checkbox
                  checked={watch('is_submittable')}
                  onChange={(e) =>
                    setValue('is_submittable', e.target.checked, { shouldDirty: true })
                  }
                />
                <Body2>Show submit button</Body2>
              </Label>
            </div>
            <StaffOnlyWrapper type={StaffOnlyBadgeType.STAFF}>
              <div className='mt-6'>
                <Label className='flex gap-1'>
                  <Checkbox
                    checked={watch('is_hide')}
                    onChange={(e) => setValue('is_hide', e.target.checked, { shouldDirty: true })}
                  />
                  <Body2>Hide Card</Body2>
                </Label>
              </div>
            </StaffOnlyWrapper>
          </div>
        </div>
        <div className='shrink-0'>
          <div className='flex justify-end gap-2 px-[12px] pb-[22px] pt-[12px]'>
            <div className='flex'>
              <Button.Reverse
                type='button'
                onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
                disabled={isPending}
              >
                Cancel
              </Button.Reverse>
            </div>
            <div className='flex w-[80px]'>
              <Button disabled={isPending}>{isPending ? <LoadingSpinner /> : 'Save'}</Button>
            </div>
          </div>
        </div>
      </form>
      <AvoSlideCloseModal
        open={closeModalOpen}
        onContinue={() => setCloseModalOpen(false)}
        onDiscard={onCancel}
      />
      <SlidePanel open={triggerFormOpen} onClose={() => setTriggerFormOpen(false)}>
        <SituationBuilder {...triggerComponentProps} />
      </SlidePanel>
      <SlidePanel open={customNumbersFormOpen} onClose={() => setCustomNumbersFormOpen(false)}>
        <CustomNumbersForm
          customNumberId={customNumberId}
          onCancel={() => setCustomNumbersFormOpen(false)}
          onSave={(data) => {
            setCustomNumbersFormOpen(false);
            if (!!customNumberId) {
              setValue(
                'panel_item',
                watch('panel_item').map((item) =>
                  item.id === data.id
                    ? {
                        ...item,
                        title: data.name,
                        display_value: data.display_value,
                      }
                    : item
                )
              );
            } else {
              setValue(
                'panel_item',
                [
                  ...watch('panel_item'),
                  {
                    numeric_type: 'custom',
                    id: data.id,
                    title: data.name,
                    is_optional: false,
                    display_value: data.display_value,
                  },
                ],
                { shouldDirty: true }
              );
            }
          }}
        />
      </SlidePanel>
    </>
  );
};

interface PopoverTriggerButtonProps {
  title: string;
}

const PopoverTriggerButton = ({ title }: PopoverTriggerButtonProps) => {
  return (
    <Button.Outline className='flex !w-fit items-center gap-1 !border-gray-300' type='button'>
      <Plus className='text-gray-700' strokeWidth={3} size={16} />
      <span className='text-button-1 text-gray-700'>{title}</span>
    </Button.Outline>
  );
};

const PopoverContainer = ({ children }: { children?: React.ReactNode }) => {
  return (
    <div className='z-[100] w-[340px] space-y-4 rounded-lg border border-gray-300 bg-white p-4'>
      {children}
    </div>
  );
};

const CustomNumberButton = ({ onCustomNumberCreate }) => {
  const { module } = useContext(ModuleContext);
  const { watch, setValue, clearErrors } = useFormContext();
  const [selected, setSelected] = useState<number | undefined>();

  const { data: customNumbers = [], isLoading } = useQuery<
    { id: number; title: string; display_value: string }[]
  >({
    queryKey: [module?.type, module?.id, 'customNumbers'],
    queryFn: NumbersAPI.getCustomNumbers,
    enabled: !!module,
    select: (data) =>
      data.map((item: any) => ({
        id: item.id,
        title: item.name,
        display_value: item.display_value,
      })),
  });

  const reset = () => setSelected(undefined);
  const onAdd = () => {
    setValue(
      'panel_item',
      [
        ...watch('panel_item'),
        {
          numeric_type: 'custom',
          id: selected,
          title: customNumbers.find((item) => item.id === selected)?.title,
          is_optional: false,
          display_value: customNumbers.find((item) => item.id === selected)?.display_value,
        },
      ],
      { shouldDirty: true }
    );
    clearErrors('panel_item');
    reset();
  };
  return (
    <Popover placement='bottom-start'>
      <PopoverTrigger>
        <PopoverTriggerButton title='Custom' />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverContainer>
          <DropdownSingle
            placeholder='Select Custom Number'
            onChange={(value) => setSelected(+value)}
            value={selected ?? ''}
            options={customNumbers}
          />
          <PopoverClose className='w-full'>
            <Button.Outline onClick={onCustomNumberCreate}>
              <div className='flex items-center gap-1 text-gray-700'>
                <Plus className='text-gray-700' strokeWidth={1.5} size={16} />
                <Body2>Create New</Body2>
              </div>
            </Button.Outline>
          </PopoverClose>
          <div className='flex justify-end gap-2'>
            <PopoverClose onClick={reset}>
              <Button.Reverse className='!w-fit'>Cancel</Button.Reverse>
            </PopoverClose>
            <PopoverClose onClick={onAdd} disabled={!selected}>
              <div className='w-[100px]'>
                <Button disabled={!selected}>Add</Button>
              </div>
            </PopoverClose>
          </div>
        </PopoverContainer>
      </PopoverContent>
    </Popover>
  );
};

const PredefinedNumberButton = () => {
  const { watch, setValue, clearErrors } = useFormContext();
  const [selected, setSelected] = useState<number | undefined>();
  const { data: globalNumbers = [], isLoading } = useQuery<
    { id: number; title: string; display_value: string }[]
  >({
    queryKey: ['globalNumbers'],
    queryFn: NumbersAPI.getGlobalNumbers,
    select: (data) =>
      data.map((item) => ({ id: item.id, title: item.title, display_value: item.display_value })),
  });
  const reset = () => setSelected(undefined);
  const onAdd = () => {
    setValue(
      'panel_item',
      [
        ...watch('panel_item'),
        {
          numeric_type: 'predefined',
          id: selected,
          title: globalNumbers.find((item) => item.id === selected)?.title,
          is_optional: false,
          display_value: globalNumbers.find((item) => item.id === selected)?.display_value,
        },
      ],
      { shouldDirty: true }
    );
    clearErrors('panel_item');
    reset();
  };
  return (
    <Popover placement='bottom-start'>
      <PopoverTrigger>
        <PopoverTriggerButton title='Predefined' />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverContainer>
          <DropdownSingle
            placeholder='Select Predefined Number'
            onChange={(value) => setSelected(+value)}
            value={selected ?? ''}
            options={globalNumbers}
          />
          <div className='flex justify-end gap-2'>
            <PopoverClose onClick={reset}>
              <Button.Reverse className='!w-fit'>Cancel</Button.Reverse>
            </PopoverClose>
            <PopoverClose disabled={!selected} onClick={onAdd}>
              <div className='w-[100px]'>
                <Button disabled={!selected}>Add</Button>
              </div>
            </PopoverClose>
          </div>
        </PopoverContainer>
      </PopoverContent>
    </Popover>
  );
};
